import { FormEvent, useEffect, useState } from 'react';
import { Navigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { AxiosError } from 'axios';
import { useCurrentUser } from '@/hooks/useAuth';
import { authApi } from '@/lib/auth';
import { AppShell } from '@/components/layout/AppShell';
import { AvatarUploader } from '@/components/AvatarUploader';

/**
 * Account-level profile editor: avatar (file+crop+upload), display name,
 * email + sign-in linkage. Workspace-level settings live elsewhere.
 */
export function ProfilePage() {
  const { t } = useTranslation();
  const me = useCurrentUser();
  const qc = useQueryClient();

  const [name, setName] = useState('');
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (me.data) setName(me.data.name ?? '');
  }, [me.data]);

  const save = useMutation({
    mutationFn: () => authApi.updateProfile({
      name: name.trim() === '' ? undefined : name.trim(),
    }),
    onSuccess: (updated) => {
      qc.setQueryData(['auth', 'me'], updated);
      setSaved(true);
      setError(null);
      window.setTimeout(() => setSaved(false), 2400);
    },
    onError: (err) => {
      const ax = err as AxiosError<{ message?: string | string[] }>;
      const raw = ax?.response?.data?.message;
      const msg = (Array.isArray(raw) ? raw[0] : raw) ?? 'Failed to save.';
      setError(String(msg));
    },
  });

  if (me.isLoading) {
    return (
      <AppShell titleKey="profile.title">
        <p className="text-sm text-ink-500">{t('common.loading', { defaultValue: 'Loading…' })}</p>
      </AppShell>
    );
  }
  if (!me.data) return <Navigate to="/login" replace />;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    save.mutate();
  };

  return (
    <AppShell titleKey="profile.title">
      <div className="max-w-2xl space-y-6">
        <section className="rounded-2xl border border-ink-200 bg-white p-6 space-y-5">
          <AvatarUploader user={me.data} />
          <div className="border-t border-ink-200 pt-3">
            <div className="text-sm text-ink-700 truncate">{me.data.email}</div>
            <div className="mt-1.5 text-[11px] uppercase tracking-[0.12em] text-ink-500">
              {t(`profile.role_${me.data.role ?? 'member'}`, { defaultValue: me.data.role ?? 'member' })}
            </div>
          </div>
        </section>

        <form onSubmit={onSubmit} className="rounded-2xl border border-ink-200 bg-white p-6 space-y-4">
          <h2 className="text-base font-semibold text-ink-900">
            {t('profile.basics', { defaultValue: 'Basics' })}
          </h2>
          <div>
            <label htmlFor="display-name" className="block text-sm font-medium text-ink-900 mb-1">
              {t('profile.display_name', { defaultValue: 'Display name' })}
            </label>
            <input
              id="display-name"
              value={name}
              onChange={(e) => setName(e.target.value)}
              maxLength={120}
              className="w-full h-10 rounded-lg border border-ink-300 bg-white px-3 text-sm text-ink-900 focus:outline-none focus:ring-2 focus:ring-ink-900/15 focus:border-ink-900"
            />
            <p className="mt-1 text-[12px] text-ink-500">
              {t('profile.display_name_hint', { defaultValue: 'How teammates see you in mentions and audit logs.' })}
            </p>
          </div>

          {error && (
            <div role="alert" className="rounded-lg border border-red-200 bg-red-50 text-red-700 px-3 py-2 text-[13px]">{error}</div>
          )}
          {saved && (
            <div role="status" className="rounded-lg border border-emerald-200 bg-emerald-50 text-emerald-700 px-3 py-2 text-[13px]">
              {t('profile.saved', { defaultValue: 'Saved.' })}
            </div>
          )}

          <div className="flex justify-end pt-2">
            <button
              type="submit"
              disabled={save.isPending}
              className="rounded-lg bg-ink-900 text-white px-4 py-2 text-sm font-medium hover:bg-ink-800 disabled:opacity-50"
            >
              {save.isPending ? t('common.saving', { defaultValue: 'Saving…' }) : t('common.save_changes', { defaultValue: 'Save changes' })}
            </button>
          </div>
        </form>
      </div>
    </AppShell>
  );
}
